import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, RefreshCw, Play, Undo2, CheckCircle, AlertTriangle, Clock, AlertCircle, X } from 'lucide-react';
import { Button } from '../components/ui/button';
import { toast } from '../hooks/use-toast';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '../components/ui/dialog';
import api from '../services/api';

const STATUS_CONFIG = {
  pending:     { label: 'Pending',      icon: Clock,        cls: 'bg-amber-100 text-amber-700 border-amber-300' },
  installed:   { label: 'Installed',    icon: CheckCircle,  cls: 'bg-emerald-100 text-emerald-700 border-emerald-300' },
  rolled_back: { label: 'Rolled Back',  icon: Undo2,        cls: 'bg-gray-100 text-gray-600 border-gray-300' },
  failed:      { label: 'Failed',       icon: AlertCircle,  cls: 'bg-red-100 text-red-700 border-red-300' },
};

const Migrations = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [items, setItems] = useState([]);
  const [selected, setSelected] = useState(null);   // migration currently in confirm-dialog
  const [action, setAction] = useState(null);       // 'install' | 'rollback' | null
  const [previewNote, setPreviewNote] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const res = await api.get('/admin/migrations');
      setItems(res.data.migrations || []);
    } catch (e) {
      toast({
        title: 'Failed to load migrations',
        description: e?.response?.data?.detail || e.message,
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const openConfirm = async (mig, act) => {
    setSelected(mig);
    setAction(act);
    setPreviewNote(null);
    if (act === 'install') {
      // Fetch a preview so the admin sees the row count before confirming
      try {
        const res = await api.post(`/admin/migrations/${mig.id}/preview`);
        setPreviewNote(res.data.preview);
      } catch (e) {
        setPreviewNote({ will_change: '?', note: `Preview failed: ${e?.response?.data?.detail || e.message}` });
      }
    }
  };

  const closeConfirm = () => {
    if (busy) return;
    setSelected(null);
    setAction(null);
    setPreviewNote(null);
  };

  const runAction = async () => {
    if (!selected || !action) return;
    setBusy(true);
    try {
      const res = await api.post(`/admin/migrations/${selected.id}/${action}`);
      const r = res.data.result || {};
      toast({
        title: action === 'install' ? 'Installed' : 'Rolled back',
        description: r.note || 'Done.',
      });
      closeConfirm();
      await load();
    } catch (e) {
      toast({
        title: `${action === 'install' ? 'Install' : 'Rollback'} failed`,
        description: e?.response?.data?.detail || e.message,
        variant: 'destructive',
      });
    } finally {
      setBusy(false);
    }
  };

  if (loading && items.length === 0) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="w-12 h-12 border-4 border-slate-500 border-t-transparent rounded-full animate-spin mx-auto" />
          <p className="text-gray-500 mt-4">Loading migrations…</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50" data-testid="migrations-page">
      {/* Header */}
      <div className="bg-slate-800 text-white sticky top-0 z-10">
        <div className="px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="icon" onClick={() => navigate(-1)} className="text-white hover:bg-slate-700" data-testid="back-btn">
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div>
              <h1 className="text-lg font-semibold">Data Migrations</h1>
              <p className="text-xs text-slate-300">One-time data fixes for this database</p>
            </div>
          </div>
          <Button variant="ghost" size="icon" onClick={load} className="text-white hover:bg-slate-700" data-testid="refresh-btn" disabled={loading}>
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
          </Button>
        </div>
      </div>

      <div className="p-3 lg:p-4 max-w-4xl mx-auto">
        {/* Helper card */}
        <div className="bg-white rounded-xl border p-3 mb-3 flex gap-3">
          <div className="w-9 h-9 rounded-full bg-slate-100 flex items-center justify-center flex-shrink-0">
            <AlertTriangle className="w-4 h-4 text-slate-600" />
          </div>
          <div className="text-xs text-gray-600 leading-relaxed">
            <b>How this works:</b> Each row is a one-time data fix. Press <b>Install</b> to apply it to <b>this</b> database. Every database (preview, production) has its own independent status — installing here doesn&apos;t touch production, and vice-versa. Installs are safe to repeat: nothing bad happens if you press twice.
          </div>
        </div>

        {items.length === 0 ? (
          <div className="bg-white rounded-xl shadow p-10 text-center text-gray-500" data-testid="empty-state">
            <CheckCircle className="w-10 h-10 text-gray-300 mx-auto mb-3" />
            <p className="text-sm">No migrations registered yet.</p>
          </div>
        ) : (
          <div className="space-y-3">
            {items.map((m) => {
              const cfg = STATUS_CONFIG[m.status] || STATUS_CONFIG.pending;
              const StatusIcon = cfg.icon;
              return (
                <div key={m.id} className="bg-white rounded-xl border shadow-sm p-4" data-testid={`migration-${m.id}`}>
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2 flex-wrap">
                        <h3 className="font-semibold text-gray-900" data-testid={`migration-name-${m.id}`}>{m.name}</h3>
                        <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full border inline-flex items-center gap-1 ${cfg.cls}`} data-testid={`migration-status-${m.id}`}>
                          <StatusIcon className="w-3 h-3" />
                          {cfg.label}
                        </span>
                      </div>
                      <p className="text-xs text-gray-500 mt-1">{m.description}</p>
                      <div className="mt-2 flex items-center gap-3 text-[11px] text-gray-400">
                        <span>Written: {m.created_at}</span>
                        {m.last_action_at && (
                          <span>Last: {new Date(m.last_action_at).toLocaleString('en-IN', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' })} by {m.last_action_by || '—'}</span>
                        )}
                      </div>
                      {m.status === 'failed' && m.last_result?._error && (
                        <div className="mt-2 text-[11px] text-red-700 bg-red-50 border border-red-200 rounded p-2" data-testid={`error-${m.id}`}>
                          <b>Error:</b> {m.last_result._error}
                        </div>
                      )}
                    </div>
                    <div className="flex flex-col gap-2 flex-shrink-0">
                      {(m.status === 'pending' || m.status === 'failed' || m.status === 'rolled_back') && (
                        <Button size="sm" onClick={() => openConfirm(m, 'install')} className="bg-emerald-600 hover:bg-emerald-700 text-white text-xs h-8" data-testid={`install-${m.id}`}>
                          <Play className="w-3 h-3 mr-1" /> Install
                        </Button>
                      )}
                      {m.status === 'installed' && (
                        <Button size="sm" variant="outline" disabled className="text-xs h-8 border-emerald-200 text-emerald-700 opacity-60" data-testid={`installed-badge-${m.id}`}>
                          <CheckCircle className="w-3 h-3 mr-1" /> Done
                        </Button>
                      )}
                      {m.status === 'installed' && m.supports_rollback && (
                        <Button size="sm" variant="outline" onClick={() => openConfirm(m, 'rollback')} className="text-xs h-8 text-red-700 border-red-300 hover:bg-red-50" data-testid={`rollback-${m.id}`}>
                          <Undo2 className="w-3 h-3 mr-1" /> Rollback
                        </Button>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Confirm dialog */}
      <Dialog open={!!selected} onOpenChange={(open) => !open && closeConfirm()}>
        <DialogContent data-testid="confirm-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              {action === 'install' ? <Play className="w-4 h-4 text-emerald-600" /> : <Undo2 className="w-4 h-4 text-red-600" />}
              {action === 'install' ? 'Install migration?' : 'Rollback migration?'}
            </DialogTitle>
            <DialogDescription className="pt-2">
              <b>{selected?.name}</b>
            </DialogDescription>
          </DialogHeader>
          <div className="text-sm text-gray-600 leading-relaxed">
            {action === 'install' ? (
              previewNote ? (
                <>
                  <div className="bg-slate-50 border rounded p-3 text-xs" data-testid="preview-note">
                    <div className="font-semibold text-slate-800 mb-1">
                      About to change {previewNote.will_change} row{previewNote.will_change === 1 ? '' : 's'}.
                    </div>
                    <div className="text-gray-600">{previewNote.note}</div>
                  </div>
                  {selected?.danger_note && (
                    <div className="mt-2 bg-red-50 border border-red-200 rounded p-3 text-xs text-red-700">
                      <b>Heads up:</b> {selected.danger_note}
                    </div>
                  )}
                </>
              ) : (
                <div className="text-xs text-gray-500">Checking how many rows will change…</div>
              )
            ) : (
              <div className="bg-red-50 border border-red-200 rounded p-3 text-xs text-red-700">
                This will undo the migration on <b>this database only</b>. You can re-install anytime.
              </div>
            )}
          </div>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={closeConfirm} disabled={busy} data-testid="cancel-btn">
              <X className="w-3 h-3 mr-1" /> Cancel
            </Button>
            <Button
              onClick={runAction}
              disabled={busy || (action === 'install' && !previewNote)}
              className={action === 'install' ? 'bg-emerald-600 hover:bg-emerald-700' : 'bg-red-600 hover:bg-red-700'}
              data-testid="confirm-btn"
            >
              {busy ? (
                <><RefreshCw className="w-3 h-3 mr-1 animate-spin" /> Working…</>
              ) : action === 'install' ? (
                <><Play className="w-3 h-3 mr-1" /> Yes, Install</>
              ) : (
                <><Undo2 className="w-3 h-3 mr-1" /> Yes, Rollback</>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Migrations;
